import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import {
  Card,
  Segmented,
  Spin,
  Row,
  Col,
  Typography,
  Progress,
  Space,
  Input,
  Select,
  DatePicker,
  Table,
  Modal,
  Avatar,
  Tag,
  Button,
  Tooltip
} from "antd";
import {
  BarChartOutlined,
  SearchOutlined,
  TeamOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { Text } = Typography;
const { Option } = Select;

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR  = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

/* ─── Zone & Medal Helpers ─── */
const getZone = (rate) => {
  if (rate >= 90) return { stroke: "#10b981", light: "#d1fae5", text: "#065f46", label: "Excellent" };
  if (rate >= 75) return { stroke: "#3b82f6", light: "#dbeafe", text: "#1e3a8a", label: "Good" };
  if (rate >= 60) return { stroke: "#f59e0b", light: "#fef3c7", text: "#78350f", label: "Average" };
  return           { stroke: "#ef4444", light: "#fee2e2", text: "#7f1d1d", label: "Critical" };
};

const medalLabel = (rank) => {
  if (rank === 1) return { icon: "🥇", bg: "#fef9c3", color: "#854d0e" };
  if (rank === 2) return { icon: "🥈", bg: "#f1f5f9", color: "#334155" };
  if (rank === 3) return { icon: "🥉", bg: "#fff7ed", color: "#7c2d12" };
  return { icon: `#${rank}`, bg: "#f8fafc", color: "#64748b" };
};

/* ==============================================================
   DEPARTMENT EMPLOYEE ROSTER MODAL (DRILL-DOWN)
============================================================== */
function DepartmentRosterModal({ open, onClose, department }) {
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!open) setSearchText("");
  }, [open]);

  const employees = department?.employees || [];

  const filteredEmployees = useMemo(() => {
    if (!employees || employees.length === 0) return [];
    if (!searchText.trim()) return employees;
    const q = searchText.trim().toLowerCase();
    return employees.filter((e) => {
      const name = (e.employee_name || "").toLowerCase();
      const code = (e.emp_code || "").toLowerCase();
      const desig = (e.designation || "").toLowerCase();
      return name.includes(q) || code.includes(q) || desig.includes(q);
    });
  }, [employees, searchText]);

  if (!department) return null;

  const z = getZone(department.rate ?? 0);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={780}
      styles={{ body: { padding: "18px 24px 24px" } }}
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: "linear-gradient(135deg, #0e497b 0%, #1d4ed8 100%)",
              color: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
            }}>
              <TeamOutlined />
            </div>
            <div>
              <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>
                {department.name} Department Roster
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                {department.total_staff} Active Employees · Working Days: {department.working_days ?? 1}
              </div>
            </div>
          </div>
          <Tag style={{
            borderRadius: 12, padding: "4px 12px", fontWeight: 800, fontSize: 13,
            background: z.light, color: z.text, border: `1px solid ${z.stroke}40`
          }}>
            Benchmark Rate: {department.rate}%
          </Tag>
        </div>
      }
    >
      {/* Search Input Bar */}
      <div style={{ marginBottom: 14, marginTop: 4 }}>
        <Input
          placeholder="Search employee by Name, Employee Code, Designation..."
          prefix={<SearchOutlined style={{ color: "#94a3b8", fontSize: 15 }} />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          size="large"
          style={{
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            boxShadow: "0 1px 4px rgba(15,23,42,0.04)",
            fontSize: 13
          }}
        />
      </div>

      {filteredEmployees.length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "#94a3b8" }}>
          No employee records found in {department.name} for current filter.
        </div>
      ) : (
        <div style={{ maxHeight: 460, overflowY: "auto", paddingRight: 4 }}>
          {filteredEmployees.map((emp, idx) => {
            const ez = getZone(emp.rate ?? 0);
            return (
              <div
                key={emp.employee_id || idx}
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid #f1f5f9",
                  marginBottom: 8,
                  background: idx % 2 === 0 ? "#f8fafc" : "#ffffff",
                  transition: "all 0.18s ease-in-out",
                  boxShadow: "0 1px 3px rgba(15,23,42,0.02)"
                }}
              >
                <Avatar
                  src={emp.photo}
                  icon={<UserOutlined />}
                  size={42}
                  style={{
                    flexShrink: 0,
                    background: "linear-gradient(135deg, #0e497b20, #0e497b40)",
                    border: "1.5px solid #e2e8f0"
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 800, fontSize: 13.5, color: "#0f172a" }}>
                      {emp.employee_name}
                    </span>
                    <Tag style={{
                      fontSize: 10, fontWeight: 700, borderRadius: 6,
                      background: "#e2e8f0", border: "none", color: "#334155",
                      padding: "0 6px"
                    }}>
                      {emp.emp_code}
                    </Tag>
                  </div>
                  <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                    {emp.designation}
                  </div>
                </div>

                {/* Sub-stats */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginRight: 10 }}>
                  <Tooltip title="Attended Days">
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#16a34a", background: "#f0fdf4", padding: "2px 8px", borderRadius: 6, border: "1px solid #bbf7d0" }}>
                      <CheckCircleOutlined style={{ marginRight: 4 }} />
                      {emp.attended_days}d
                    </span>
                  </Tooltip>
                  {emp.late_days > 0 && (
                    <Tooltip title="Late In Days">
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#d97706", background: "#fffbeb", padding: "2px 8px", borderRadius: 6, border: "1px solid #fde68a" }}>
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        {emp.late_days}l
                      </span>
                    </Tooltip>
                  )}
                  {emp.leave_days > 0 && (
                    <Tooltip title="Leave Days">
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#2563eb", background: "#eff6ff", padding: "2px 8px", borderRadius: 6, border: "1px solid #bfdbfe" }}>
                        <CalendarOutlined style={{ marginRight: 4 }} />
                        {emp.leave_days}lv
                      </span>
                    </Tooltip>
                  )}
                  {(() => {
                    const empAbsent = (emp.absent_days !== undefined && emp.absent_days !== null && emp.absent_days > 0)
                      ? emp.absent_days
                      : Math.max(0, (emp.eligible_days || 1) - ((emp.attended_days || 0) + (emp.leave_days || 0)));
                    return empAbsent > 0 ? (
                      <Tooltip title="Absent Days">
                        <span style={{ fontSize: 12, fontWeight: 800, color: "#dc2626", background: "#fef2f2", padding: "2px 8px", borderRadius: 6, border: "1px solid #fecaca" }}>
                          <CloseCircleOutlined style={{ marginRight: 4 }} />
                          {empAbsent}a
                        </span>
                      </Tooltip>
                    ) : null;
                  })()}
                </div>

                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <Tag style={{
                    borderRadius: 8, padding: "3px 10px", fontWeight: 900, fontSize: 12,
                    background: ez.light, color: ez.text, border: `1px solid ${ez.stroke}40`
                  }}>
                    {emp.rate}%
                  </Tag>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

/* ==============================================================
   MAIN COMPONENT
============================================================== */
export default function DepartmentAttendanceBenchmark({ selectedDate: externalDate, selectedOrg: externalOrg, selectedDept: externalDept }) {
  const { authData, backUrl } = useContext(AuthContext) || {};

  const [loading, setLoading]             = useState(true);
  const [deptData, setDeptData]           = useState([]);
  const [companyAvg, setCompanyAvg]       = useState(0);
  const [totalWorkingDays, setTotalWorkingDays] = useState(1);
  const [organisations, setOrganisations] = useState([]);
  const [orgId, setOrgId]                 = useState(externalOrg || "ALL");
  const [dateMode, setDateMode]           = useState(externalDate ? "custom" : "month");
  const [month, setMonth]                 = useState(CURRENT_MONTH);
  const [year, setYear]                   = useState(CURRENT_YEAR);
  const [customDate, setCustomDate]       = useState(externalDate ? dayjs(externalDate, "YYYY-MM-DD") : dayjs());
  const [selectedDept, setSelectedDept]   = useState(externalDept || "ALL");
  const [sortOrder, setSortOrder]         = useState("desc");
  const [searchQuery, setSearchQuery]     = useState("");

  // Roster Modal state
  const [rosterModalOpen, setRosterModalOpen] = useState(false);
  const [activeDept, setActiveDept]         = useState(null);

  // Sync external filter props from HrIndex
  useEffect(() => {
    if (externalDate) {
      const parsed = dayjs(externalDate, "YYYY-MM-DD");
      if (parsed.isValid()) {
        setCustomDate(parsed);
        setDateMode("custom");
      }
    }
  }, [externalDate]);

  useEffect(() => {
    if (externalOrg !== undefined) {
      setOrgId(externalOrg);
    }
  }, [externalOrg]);

  useEffect(() => {
    if (externalDept !== undefined) {
      setSelectedDept(externalDept);
    }
  }, [externalDept]);

  /* fetch organisations */
  useEffect(() => {
    if (!authData?.access) return;
    const apiBase = backUrl || "";
    axios
      .get(`${apiBase}/api/emp/organisations/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      })
      .then((r) => setOrganisations(r.data.results || r.data || []))
      .catch(() => {});
  }, [backUrl, authData?.access]);

    /* fetch benchmark data */
  useEffect(() => {
    if (!authData?.access) return;
    setLoading(true);
    const apiBase = backUrl || "";
    const params = {};
    if (orgId && orgId !== "ALL") params.organisation_id = orgId;

    if (dateMode === "custom" && customDate) {
      params.date = customDate.format("YYYY-MM-DD");
    } else {
      params.month = month;
      params.year  = year;
    }

    axios
      .get(`${apiBase}/api/reports/department-attendance-benchmark/`, {
        params,
        headers: { Authorization: `Bearer ${authData?.access}` },
      })
      .then((r) => {
        setDeptData(r.data?.departments || []);
        setCompanyAvg(r.data?.company_avg ?? 0);
        setTotalWorkingDays(r.data?.total_working_days ?? 1);
      })
      .catch(() => {
        setDeptData([]);
        setCompanyAvg(0);
        setTotalWorkingDays(1);
      })
      .finally(() => setLoading(false));
  }, [orgId, month, year, dateMode, customDate, backUrl, authData?.access]);

  /* derived calculations */
  const byRate     = [...deptData].sort((a, b) => b.rate - a.rate);
  const top        = byRate[0];
  const bottom     = byRate.length > 1 ? byRate[byRate.length - 1] : null;
  const avg        = companyAvg;
  const totalStaff = deptData.reduce((s, d) => s + (d.total_staff || 0), 0);

  const filtered = useMemo(() => {
    let list = deptData;
    if (selectedDept && selectedDept !== "ALL") {
      const matchStr = String(selectedDept).toLowerCase();
      list = list.filter((d) => 
        String(d.id) === matchStr || 
        (d.name || "").toLowerCase() === matchStr
      );
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((d) => (d.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [deptData, selectedDept, searchQuery]);

  const tableData = useMemo(() => {
    return [...filtered]
      .sort((a, b) => (sortOrder === "desc" ? b.rate - a.rate : a.rate - b.rate))
      .map((d, i) => ({ ...d, rank: i + 1, key: d.id || i }));
  }, [filtered, sortOrder]);

  const periodLabel = dateMode === "custom"
    ? customDate.format("DD MMM YYYY")
    : `${MONTHS[month - 1]} ${year}`;

  const openRoster = (dept) => {
    setActiveDept(dept);
    setRosterModalOpen(true);
  };

  /* ─── Table columns ─── */
  const columns = [
    {
      title: "Rank",
      dataIndex: "rank",
      width: 65,
      align: "center",
      render: (rank) => {
        const m = medalLabel(rank);
        return (
          <span style={{
            background: m.bg, color: m.color,
            borderRadius: 8, padding: "2px 9px",
            fontSize: 13, fontWeight: 800, whiteSpace: "nowrap",
          }}>
            {m.icon}
          </span>
        );
      },
    },
    {
      title: "Department",
      dataIndex: "name",
      render: (name, rec) => (
        <div style={{ cursor: "pointer" }} onClick={() => openRoster(rec)}>
          <div style={{ fontWeight: 800, color: "#0f172a", fontSize: 13.5 }}>{name}</div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            <TeamOutlined style={{ marginRight: 4 }} />
            {rec.total_staff} active staff · {rec.working_days ?? totalWorkingDays} working days
          </div>
        </div>
      ),
    },
    {
      title: "Attendance Breakdown",
      key: "breakdown",
      width: 220,
      render: (_, rec) => {
        const eligible = rec.eligible_staff ?? (rec.total_staff * (rec.working_days ?? totalWorkingDays ?? 1));
        const attended = rec.attended ?? (rec.present ?? 0);
        const leave = rec.on_leave ?? 0;
        const absentCount = (rec.absent !== undefined && rec.absent !== null && rec.absent > 0)
          ? rec.absent
          : Math.max(0, eligible - (attended + leave));
        const onTimePresent = rec.on_time_present !== undefined
          ? rec.on_time_present
          : (rec.late > 0 && rec.present > rec.late ? rec.present - rec.late : rec.present);

        return (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <Tag style={{ fontSize: 10.5, fontWeight: 700, background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" }}>
              Present: {onTimePresent || 0}
            </Tag>
            {rec.late > 0 && (
              <Tag style={{ fontSize: 10.5, fontWeight: 700, background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" }}>
                Late: {rec.late}
              </Tag>
            )}
            {leave > 0 && (
              <Tag style={{ fontSize: 10.5, fontWeight: 700, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>
                Leave: {leave}
              </Tag>
            )}
            {absentCount > 0 && (
              <Tag style={{ fontSize: 10.5, fontWeight: 700, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" }}>
                Absent: {absentCount}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Attendance Compliance Rate",
      dataIndex: "rate",
      width: 260,
      render: (rate) => {
        const z = getZone(rate);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ flex: 1 }}>
              <Progress
                percent={rate}
                strokeColor={z.stroke}
                trailColor="#e2e8f0"
                showInfo={false}
                size={[undefined, 10]}
                style={{ margin: 0 }}
              />
            </div>
            <span style={{
              fontWeight: 900, fontSize: 15, color: z.stroke,
              minWidth: 50, textAlign: "right",
            }}>
              {rate}%
            </span>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 90,
      align: "center",
      render: (_, rec) => (
        <Button
          type="default"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => openRoster(rec)}
          style={{
            borderRadius: 7, fontWeight: 700, fontSize: 11.5,
            borderColor: "#cbd5e1", color: "#0e497b", background: "#f8fafc"
          }}
        >
          Team
        </Button>
      ),
    },
  ];

  return (
    <Card
      variant="borderless"
      style={{
        borderRadius: 18,
        background: "#fff",
        boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
        border: "1.5px solid #cbd5e1",
      }}
      styles={{ body: { padding: "20px 22px" } }}
    >
      {/* Roster Modal */}
      <DepartmentRosterModal
        open={rosterModalOpen}
        onClose={() => setRosterModalOpen(false)}
        department={activeDept}
      />

      {/* ── HEADER + FILTERS ── */}
      <Row justify="space-between" align="middle" gutter={[0, 12]} style={{ marginBottom: 18 }}>
        <Col xs={24} md={10}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: "linear-gradient(135deg,#0e497b 0%,#2563eb 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, boxShadow: "0 4px 12px rgba(14, 73, 123, 0.2)"
            }}>
              <BarChartOutlined style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div>
              <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>
                Department Attendance Benchmark
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 1, fontWeight: 500 }}>
                Compliance performance leaderboard · {periodLabel}
              </div>
            </div>
          </div>
        </Col>

        <Col xs={24} md={14}>
          <Space wrap size={8} style={{ justifyContent: "flex-end", width: "100%" }}>
            <Select value={orgId} onChange={setOrgId} style={{ width: 160 }} size="middle">
              <Option value="ALL">All Organisations</Option>
              {organisations.map((o) => (
                <Option key={o.id} value={o.id}>{o.name}</Option>
              ))}
            </Select>

            <Segmented
              size="middle"
              value={dateMode}
              onChange={setDateMode}
              options={[
                { label: "Monthly", value: "month" },
                { label: "Custom Date", value: "custom" },
              ]}
            />

            {dateMode === "month" ? (
              <>
                <Select value={month} onChange={setMonth} style={{ width: 110 }} size="middle">
                  {MONTHS.map((m, i) => (
                    <Option key={i + 1} value={i + 1}>{m}</Option>
                  ))}
                </Select>
                <Select value={year} onChange={setYear} style={{ width: 85 }} size="middle">
                  {YEARS.map((y) => (
                    <Option key={y} value={y}>{y}</Option>
                  ))}
                </Select>
              </>
            ) : (
              <DatePicker
                allowClear={false}
                value={customDate}
                onChange={(v) => setCustomDate(v || dayjs())}
                style={{ width: 140 }}
                size="middle"
              />
            )}

            <Input
              placeholder="Search Dept..."
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 140, borderRadius: 8 }}
              size="middle"
              allowClear
            />

            <Segmented
              size="middle"
              value={sortOrder}
              onChange={setSortOrder}
              options={[
                { label: <span><ArrowDownOutlined /> High</span>, value: "desc" },
                { label: <span><ArrowUpOutlined /> Low</span>,  value: "asc"  },
              ]}
            />
          </Space>
        </Col>
      </Row>

      {/* ── KPI SUMMARY STRIP ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {/* Top Dept */}
        <Col xs={24} sm={6}>
          <div style={{
            background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
            border: "1.5px solid #bbf7d0", borderRadius: 14,
            padding: "12px 14px", minHeight: 74,
            boxShadow: "0 2px 6px rgba(16, 185, 129, 0.08)"
          }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: "#15803d", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
              🥇 Top Department
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: "#14532d", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {top?.name || "—"}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#10b981", lineHeight: 1 }}>
              {top?.rate ?? 0}%
            </div>
          </div>
        </Col>

        {/* Bottom Dept */}
        <Col xs={24} sm={6}>
          <div style={{
            background: "linear-gradient(135deg,#fff1f2,#fee2e2)",
            border: "1.5px solid #fecaca", borderRadius: 14,
            padding: "12px 14px", minHeight: 74,
            boxShadow: "0 2px 6px rgba(239, 68, 68, 0.08)"
          }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: "#b91c1c", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
              ⚠️ Needs Attention
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: "#7f1d1d", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {bottom?.name || "—"}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#ef4444", lineHeight: 1 }}>
              {bottom?.rate ?? 0}%
            </div>
          </div>
        </Col>

        {/* Company Average */}
        <Col xs={24} sm={6}>
          <div style={{
            background: "linear-gradient(135deg,#eff6ff,#dbeafe)",
            border: "1.5px solid #bfdbfe", borderRadius: 14,
            padding: "12px 14px", minHeight: 74,
            boxShadow: "0 2px 6px rgba(59, 130, 246, 0.08)"
          }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
              📊 Company Average
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#2563eb", lineHeight: 1, marginBottom: 2 }}>
              {avg}%
            </div>
            <div style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 700 }}>
              Across {deptData.length} departments
            </div>
          </div>
        </Col>

        {/* Total Workforce */}
        <Col xs={24} sm={6}>
          <div style={{
            background: "linear-gradient(135deg,#faf5ff,#ede9fe)",
            border: "1.5px solid #ddd6fe", borderRadius: 14,
            padding: "12px 14px", minHeight: 74,
            boxShadow: "0 2px 6px rgba(124, 58, 237, 0.08)"
          }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: "#7c3aed", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
              👥 Total Workforce
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#6d28d9", lineHeight: 1, marginBottom: 2 }}>
              {totalStaff}
            </div>
            <div style={{ fontSize: 11, color: "#7c3aed", fontWeight: 700 }}>
              Active Staff · {totalWorkingDays} Working Days
            </div>
          </div>
        </Col>
      </Row>

      {/* ── COLOUR LEGEND ── */}
      <div style={{ display: "flex", gap: 16, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <Text style={{ fontSize: 11.5, color: "#64748b", fontWeight: 700, marginRight: 2 }}>Zones:</Text>
        {[
          { label: "Excellent", color: "#10b981", range: "≥ 90%" },
          { label: "Good",      color: "#3b82f6", range: "75–89%" },
          { label: "Average",   color: "#f59e0b", range: "60–74%" },
          { label: "Critical",  color: "#ef4444", range: "< 60%" },
        ].map((z) => (
          <span key={z.label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "#334155" }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: z.color, display: "inline-block" }} />
            <strong style={{ color: z.color }}>{z.label}</strong> {z.range}
          </span>
        ))}
      </div>

      {/* ── LEADERBOARD TABLE ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 14, color: "#64748b", fontWeight: 700, fontSize: 13 }}>
            Calculating accurate department benchmarks…
          </div>
        </div>
      ) : (
        <Table
          dataSource={tableData}
          columns={columns}
          pagination={tableData.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
          size="small"
          rowKey="key"
          scroll={{ x: 720 }}
          locale={{
            emptyText: (
              <div style={{ padding: "40px 0", textAlign: "center", color: "#94a3b8" }}>
                <BarChartOutlined style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                <div style={{ fontWeight: 700 }}>No department benchmark data for selected filters</div>
              </div>
            ),
          }}
          onRow={(rec) => ({
            style: {
              background: rec.rank <= 3 && sortOrder === "desc"
                ? rec.rank === 1 ? "#fffef0"
                : rec.rank === 2 ? "#f8fafc"
                : "#fff8f5"
                : "#fff",
              transition: "background 0.15s",
            },
          })}
          style={{
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
          }}
        />
      )}
    </Card>
  );
}
